import { useEffect, useRef, useCallback, useState } from 'react';

/** Max reconnect cycles after a disconnect (resets on successful open). */
const WS_MAX_RECONNECT_ATTEMPTS = 5;
const WS_PING_INTERVAL = 30000;

/**
 * useWebSocket — connects to the backend WS endpoint, auto-reconnects,
 * and returns a subscribe function for event-based listeners.
 *
 * Usage:
 *   const { subscribe, connected } = useWebSocket(token);
 *   useEffect(() => subscribe('new_message', (data) => { ... }), [subscribe]);
 */
export function useWebSocket(token) {
  const wsRef = useRef(null);
  const listenersRef = useRef({});
  const reconnectTimer = useRef(null);
  const pingTimer = useRef(null);
  const attemptRef = useRef(0);
  const [connected, setConnected] = useState(false);

  const getWsUrl = useCallback(() => {
    const base = process.env.REACT_APP_BACKEND_URL || '';
    const wsBase = base.replace(/^http/, 'ws');
    // Auth is via httpOnly cookies (sent automatically by the browser).
    // No token in the URL — prevents leaking JWTs in logs/referrer headers.
    return `${wsBase}/api/ws`;
  }, []);

  const cleanup = useCallback(() => {
    clearTimeout(reconnectTimer.current);
    clearInterval(pingTimer.current);
    if (wsRef.current) {
      wsRef.current.onclose = null;
      wsRef.current.close();
      wsRef.current = null;
    }
    setConnected(false);
  }, []);

  const connect = useCallback(() => {
    if (!token) return;
    cleanup();

    try {
      const ws = new WebSocket(getWsUrl());
      wsRef.current = ws;

      ws.onopen = () => {
        attemptRef.current = 0;
        setConnected(true);
        // Keepalive: plain ping (backend) + JSON ping for proxies that prefer framed messages
        pingTimer.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: 'ping' }));
          }
        }, WS_PING_INTERVAL);
      };

      ws.onmessage = (event) => {
        try {
          const { event: evtName, data } = JSON.parse(event.data);
          if (evtName === 'pong') return;
          const handlers = listenersRef.current[evtName] || [];
          handlers.forEach(fn => {
            try { fn(data); } catch (e) { console.error('WS handler error:', e); }
          });
        } catch (e) {
          // Ignore non-JSON messages
        }
      };

      ws.onclose = () => {
        setConnected(false);
        clearInterval(pingTimer.current);
        if (attemptRef.current >= WS_MAX_RECONNECT_ATTEMPTS) {
          return;
        }
        attemptRef.current += 1;
        const delay = 2000 * attemptRef.current;
        reconnectTimer.current = setTimeout(connect, delay);
      };

      ws.onerror = (e) => {
        console.error('WS error:', e);
        ws.close();
      };
    } catch {
      // Will retry via onclose
    }
  }, [token, getWsUrl, cleanup]);

  useEffect(() => {
    connect();
    return cleanup;
  }, [connect, cleanup]);

  /**
   * Subscribe to a specific event. Returns an unsubscribe function.
   * Example: const unsub = subscribe('new_message', handler);
   */
  const subscribe = useCallback((eventName, handler) => {
    if (!listenersRef.current[eventName]) {
      listenersRef.current[eventName] = [];
    }
    listenersRef.current[eventName].push(handler);
    return () => {
      listenersRef.current[eventName] = (listenersRef.current[eventName] || []).filter(fn => fn !== handler);
    };
  }, []);

  return { subscribe, connected };
}
